import React, { useContext, useState } from 'react';
import SectionLayout from '../layouts/SectionLayout/index';
import {
  GlobalDispatchContext,
  GlobalStateContext,
} from '@src/context/GlobalContextProvider';
import { GTM } from '@src/analytics/gtm';
import { gtmEvents } from '@src/analytics/gtmEventsConstant';
import { useCustomToast } from '@components/extra/useCustomToast';
import { ELeadSquaredActivityCode } from '@src/constants/leadsquaredActivityConstants/index';
import { EButtonType } from '@src/constants/data/programs/index';
import useDataScienceProgram from '@src/hooks/useDataScienceProgram';
import { cn } from '@/lib/utils';
import Accordion from '../CurriculumV4/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocation } from '@reach/router';
import { getProgramTypeFromPath } from '@src/constants/pages';
import useCurriculumDownload from '@src/hooks/useCurriculumDownload';
import useSendActivities from '@src/hooks/useSendActivities';
import LeadGenerationButton from '@components/LeadGenerationForm/LeadGenerationButton';
import { LEAD_GEN_DOWNLOAD_CURRICULUM } from '@src/constants/LeadGenerationConstants/index';
import { EProgramTypeId } from '@src/constants/campaign';
import RawHTML from '@components/Common/RawHTML/index';
import { faPlus, faMinus } from '@fortawesome/free-solid-svg-icons';

const CurriculumV5 = React.memo(function CurriculumV5({
  dark,
  type,
  pageType,
  curriculum,
  selectedProgram,
  dreamCompanies,
  superDreamCompanies,
  advancedCurriculum,
  curriculumHeading,
  curriculumDescription,
  headingClassName,
  isTypeQA,
  isTypeFellowShipPlus,
  advancedCurriculumHeading,
  premiumCurriculumHeading = 'Premium Curriculum (Included)',
  premiumCurriculumSubtitle = 'All 8 foundation modules from the Premium track',
  advancedAddOnsHeading = 'Advanced Add-ons',
  advancedAddOnsSubtitle,
  advancedAddOnsHighlight,
  hideHeader,
  hideAdvanceCurriculumSection,
  isSDPage,
  brochureViewHref,
  ...props
}) {
  const { downloadCurriculum } = useCurriculumDownload();
  const [toggle, setToggle] = useState('Advanced');
  const location = useLocation();
  const pathname = location.pathname;

  const isAcceleratePage =
    location?.pathname === '/provisional-enrollment/accelerate/';

  const programType = getProgramTypeFromPath(pathname);

  const { isDataSciencePage, isDataScienceProvisionalPage } =
    useDataScienceProgram();

  const currentCurriculum =
    toggle === 'Premium' || selectedProgram?.id === EProgramTypeId.DA
      ? curriculum
      : advancedCurriculum;
  const showBothCurriculums = toggle === 'Advanced';

  const { sendActivityHandler } = useSendActivities();

  const dispatch = useContext(GlobalDispatchContext);
  const state = useContext(GlobalStateContext);
  const downloadCurriculumType = isTypeFellowShipPlus
    ? 'FELLOWSHIP_PLUS'
    : isTypeQA
    ? 'QA_AUTOMATION_TESTING'
    : isDataSciencePage
    ? 'DATA_ANALYTICS'
    : 'FELLOWSHIP';

  const triggerSuccessToast = useCustomToast(
    'success',
    <div className="">
      <h2 className="font-base font-rubik text-lg">Success!</h2>
      <p className="font-rubik text-xs">
        Your curiculum is downloaded successfully.
      </p>
    </div>,
  );

  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const triggerCurriculumWebengageFlow = (email, phone, type) => {
    GTM.login({
      userId: email,
      email: email,
      phone: phone,
      whatsappNumber: phone,
    });
    GTM.setEmail(email);

    if (phone) {
      GTM.track(gtmEvents.PHONE_SUBMITTED, {
        phone: phone,
        type: 'DOWNLOAD_CURRICULUM_STRIP',
      });
    }

    window.webengage &&
      window.webengage.user.setAttribute('we_whatsapp_opt_in', true);

    if (!state.isEmailEventSubmitted) {
      GTM.track(gtmEvents.EMAIL_SUBMITTED, {
        type: downloadCurriculumType,
        location: 'DOWNLOAD_CURRICULUM_STRIP',
      });
      dispatch({
        type: 'SET_IS_EMAIL_EVENT_SUBMITTED',
        payload: true,
      });
    }

    GTM.track(gtmEvents.DOWNLOAD_CURRICULUM_APPLIED_SUCCESSFULLY, {
      type: downloadCurriculumType,
      location: 'STRIP',
    });

    if (type == EButtonType.DOWNLOAD_CURRICULUM_BUTTON) {
      sendActivityHandler(
        ELeadSquaredActivityCode.SD_PAGE_DOWNLOAD_CURRICULUM_CLICKED,
      );
    } else {
      sendActivityHandler(
        ELeadSquaredActivityCode.SD_PAGE_DOWNLOAD_CURRICULUM_SUBMITTED,
      );
    }

    if (brochureViewHref) {
      try {
        GTM.track(gtmEvents.BROCHURE_VIEW_CTA_CLICKED, {
          type: type,
          location: 'CURRICULUM_SECTION',
          href: brochureViewHref,
        });
      } catch (err) {
        // analytics must never block navigation
      }
      window.open(brochureViewHref, '_blank', 'noopener,noreferrer');
    } else {
      downloadCurriculum();
    }
    triggerSuccessToast();
  };

  const dreamJobRange = isDataSciencePage
    ? '6 To 15 LPA'
    : props.dreamJobRange || '6 To 15 LPA';
  const superDreamJobRange =
    toggle === 'Advanced'
      ? programType === 'QA'
        ? '8 To 20 LPA'
        : props.superDreamJobRange || '15 To 40 LPA'
      : '15 To 40 LPA';

  const CurriculumAccordion = ({ curriculum, isLightTheme, index }) => {
    return (
      <Accordion
        disableGutters
        expanded={expanded === `panel${index}`}
        onChange={handleChange(`panel${index}`)}
        elevation={0}
        TransitionProps={{ unmountOnExit: false }}
        sx={{
          width: '100%',
          '& .MuiAccordion-region': {
            height: expanded === `panel${index}` ? 'auto' : '0px',
          },
          '& .MuiCollapse-wrapper': {
            width: '100%',
          },
        }}
        className={`mb-3 w-full flex-shrink-0 rounded-[10px] ${
          isLightTheme ? 'bg-v5-neutral-100' : 'bg-v5-green-700'
        }`}
      >
        <AccordionSummary
          className={`w-full overflow-hidden py-4 px-3 sm:py-6 sm:px-4 ${
            isLightTheme ? 'text-v5-neutral-600' : 'text-v5-neutral-200'
          }`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            '& .MuiAccordionSummary-content': {
              margin: '0 !important',
            },
          }}
          expandIcon={
            <FontAwesomeIcon
              icon={expanded === `panel${index}` ? faMinus : faPlus}
              className="w-[32px] transition-all"
            />
          }
          aria-controls={`panel${index}-content`}
          id={`panel${index}-header`}
        >
          <div className="flex w-full items-center justify-between gap-3">
            <h6 className="text-sm transition-all sm:text-base">
              <RawHTML>{curriculum.title}</RawHTML>
            </h6>
            <div className="flex flex-shrink-0 items-center gap-1.5 sm:gap-2">
              {curriculum.new && (
                <span className="rounded-md bg-v5-yellow-200 px-2.5 py-1.5 text-[10px] font-bold uppercase text-black sm:px-3 sm:text-xs">
                  New
                </span>
              )}
              {curriculum.duration && (
                <p className="whitespace-nowrap rounded-md bg-v5-green-200 px-2.5 py-1.5 text-[10px] font-medium text-white sm:px-3 sm:text-xs">
                  {curriculum.duration}
                </p>
              )}
            </div>
          </div>
        </AccordionSummary>
        <AccordionDetails
          className={`rounded-b-[30px] ${
            isLightTheme ? 'bg-v5-neutral-100' : 'bg-v5-green-700'
          } px-4 pt-0 pb-6 text-sm leading-relaxed text-v5-neutral-500 sm:px-6`}
        >
          <div>
            {curriculum.description && (
              <div className="mb-4 text-v5-neutral-500">
                {curriculum.description}
              </div>
            )}
            <div>
              <div className="mb-2 leading-relaxed [&_ul]:mt-0">
                <RawHTML>
                  {Array.isArray(curriculum.data)
                    ? curriculum.data.join('')
                    : curriculum.data}
                </RawHTML>
              </div>
            </div>
            {curriculum.skills && curriculum.skills.length > 0 && (
              <div className="mt-4">
                <div className="flex flex-wrap gap-2">
                  {curriculum.skills.map((skill, idx) => (
                    <div
                      key={idx}
                      className="min-w-[100px] overflow-hidden text-ellipsis whitespace-nowrap rounded-md border-2 border-black py-1 px-3 text-center font-rubik text-xs font-bold"
                    >
                      {skill}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {curriculum.footer && (
              <div className="my-4 rounded-lg bg-v5-neutral-200 p-6">
                {curriculum.footer}{' '}
              </div>
            )}
          </div>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <SectionLayout
      headingClassName={headingClassName}
      heading={''}
      description={''}
      className="md:py-0"
      {...props}
    >
      <div className="container">
        {!props.hideCurriculumSection && (
          <div
            className={cn(
              'rounded-[40px] p-4 sm:p-6 md:rounded-[50px]',
              `${
                !dark
                  ? 'bg-[#E6F4F2] text-v5-neutral-600'
                  : 'bg-v5-green-500 text-white'
              }`,
            )}
          >
            {programType === 'SKILLQ_FULL_STACK_DEVELOPMENT_PROGRAM' ? (
              <h1 className="my-10 text-center font-manrope text-2xl font-extrabold capitalize leading-tight sm:text-4xl">
                Crio SkillQ <br className="hidden sm:block"></br> Gen-AI Powered
                Full Stack Program
              </h1>
            ) : programType === 'SKILLQ_DATA_ANALYTICS_PROGRAM' ? (
              <h1 className="my-10 text-center font-manrope text-2xl font-extrabold capitalize leading-tight sm:text-4xl">
                Crio SkillQ <br className="hidden sm:block"></br> Gen-AI Powered
                Data Analytics & Data Science Program
              </h1>
            ) : (
              <>
                <h1 className="my-6 text-center font-manrope text-lg font-extrabold capitalize leading-tight sm:text-4xl">
                  {curriculumHeading ? (
                    <RawHTML>{curriculumHeading}</RawHTML>
                  ) : (
                    'Curriculum to crack dream jobs in top Product-Based companies'
                  )}
                </h1>
                <p className="text-md mb-6 text-center font-rubik font-medium text-v5-neutral-500">
                  {curriculumDescription ? (
                    <RawHTML>{curriculumDescription}</RawHTML>
                  ) : (
                    'Two tracks. Same foundation. Pick the depth that matches your career goal.'
                  )}
                </p>
              </>
            )}
            {advancedCurriculum ? (
              <div className="mb-5 flex items-center justify-center">
                <div
                  className={cn(
                    'flex h-[40px] w-full max-w-[350px] rounded-[8px] p-2 md:h-[72px] md:max-w-[480px] md:rounded-[10px]',
                    `${dark ? 'bg-v5-green-700' : 'bg-white'}`,
                  )}
                >
                  {['Premium', 'Advanced'].map((menu) => (
                    <div
                      key={menu}
                      className={cn(
                        'flex h-full flex-1 cursor-pointer flex-col items-center justify-center rounded-[8px] px-4 md:w-[210px] md:rounded-[10px]',
                      )}
                      style={
                        toggle === menu
                          ? {
                              background:
                                'linear-gradient(to right, #02BF91, #015944)',
                            }
                          : {}
                      }
                      onClick={() => {
                        setToggle(menu);
                        setExpanded(false);
                      }}
                    >
                      <div className="flex items-center gap-1 md:gap-2">
                        <h3
                          className={cn(
                            'text-[14px] font-semibold leading-none md:text-[18px]',
                            `${toggle === menu ? 'text-white' : ''}`,
                          )}
                        >
                          {isAcceleratePage
                            ? menu === 'Premium'
                              ? 'MERN Developer'
                              : 'JAVA Developer'
                            : menu}
                        </h3>
                        {menu === 'Advanced' && (
                          <span className="flex-shrink-0 rounded-md bg-v5-yellow-200 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none text-black md:px-2 md:py-1 md:text-[12px]">
                            <span className="md:hidden">★</span>
                            <span className="hidden md:inline">Recommended</span>
                          </span>
                        )}
                      </div>
                      {menu === 'Premium' && isAcceleratePage && (
                        <i className="text-[6px] md:text-[9px]">6 Months</i>
                      )}
                      {menu === 'Advanced' && isAcceleratePage && (
                        <i className="text-[6px] md:text-[9px]">6 Months</i>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <></>
            )}
            <div className="flex flex-col items-stretch justify-center">
              {showBothCurriculums ? (
                <>
                  <div className="mb-6 flex flex-col gap-3 sm:mb-8 sm:flex-row sm:items-center sm:gap-4">
                    <h3 className="w-fit rounded-md bg-v5-green-200 px-3 py-1 text-sm font-semibold text-white sm:text-base">
                      <RawHTML>{premiumCurriculumHeading}</RawHTML>
                    </h3>
                    <div className="hidden flex-1 border-t border-current opacity-30 sm:block"></div>
                    <p className="text-xs opacity-70 sm:text-sm">
                      <RawHTML>{premiumCurriculumSubtitle}</RawHTML>
                    </p>
                  </div>
                  {curriculum?.map((curriculum, index) => (
                    <CurriculumAccordion
                      key={`premium-${index}`}
                      index={`premium-${index}`}
                      curriculum={curriculum}
                      isLightTheme={!dark}
                    />
                  ))}
                  <div className="mb-6 mt-10 flex flex-col gap-3 sm:mb-8 sm:mt-12 sm:flex-row sm:items-center sm:gap-4">
                    <h3 className="w-fit rounded-md bg-v5-yellow-200 px-3 py-1 text-sm font-semibold text-black sm:text-base">
                      <RawHTML>{advancedAddOnsHeading}</RawHTML>
                    </h3>
                    <div className="hidden flex-1 border-t border-current opacity-30 sm:block"></div>
                    <p className="text-xs opacity-70 sm:text-sm">
                      {advancedAddOnsSubtitle ? (
                        <RawHTML>{advancedAddOnsSubtitle}</RawHTML>
                      ) : pageType === 'FSD' ? (
                        'Five additional modules — exclusive to the Advanced track'
                      ) : (
                        'Only in the Advanced track'
                      )}
                    </p>
                  </div>
                  <div
                    className="mb-6 rounded-lg border-2 border-dashed border-[#FFD500] p-4"
                    style={{ backgroundColor: 'rgba(255, 213, 0, 0.2)' }}
                  >
                    <p className="text-sm leading-relaxed">
                      {advancedAddOnsHighlight ? (
                        <RawHTML>{advancedAddOnsHighlight}</RawHTML>
                      ) : pageType === 'FSD' ? (
                        'The Advanced track is built for engineers targeting top product-based companies. It layers System Design (LLD & HLD), Advanced Interview Blitz, and two cutting-edge AI Engineering modules on top of the Premium foundation.'
                      ) : (
                        <>
                          <span className="font-bold">
                            Why these add-ons matter:{' '}
                          </span>
                          This is where you stop being a Selenium tester and become an SDET. You’ll test live distributed systems on AWS, and you’ll master the two stacks product companies are actually hiring for in 2026 — Playwright with TypeScript, and AI engineering (LLMs, RAG, Agents, MCP). Most QA programs teach one language and one framework. You’ll finish with two of each, and the judgement to know which to reach for.
                        </>
                      )}
                    </p>
                  </div>
                  {advancedCurriculum?.map((curriculum, index) => (
                    <CurriculumAccordion
                      key={`advanced-${index}`}
                      index={`advanced-${index}`}
                      curriculum={curriculum}
                      isLightTheme={!dark}
                    />
                  ))}
                </>
              ) : (
                <>
                  {currentCurriculum?.map((curriculum, index) => (
                    <CurriculumAccordion
                      key={index}
                      curriculum={curriculum}
                      isLightTheme={!dark}
                      index={index}
                    />
                  ))}
                </>
              )}
            </div>
          </div>
        )}
        {brochureViewHref && (
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <LeadGenerationButton
              text="View Curriculum"
              isOnePageForm
              formHeading="Program Curriculum"
              pageOneButtonText="View Now"
              buttonLocation={LEAD_GEN_DOWNLOAD_CURRICULUM}
              onSubmitPageOne={triggerCurriculumWebengageFlow}
              {...(state.email &&
                state.phone && {
                  onClick: () => {
                    triggerCurriculumWebengageFlow(
                      state.email,
                      state.phone,
                      EButtonType.DOWNLOAD_CURRICULUM_BUTTON,
                    );
                  },
                })}
            />
          </div>
        )}
      </div>
    </SectionLayout>
  );
});

export default CurriculumV5;
